import { useCallback, useEffect, useRef, useState } from 'react';
import { getGitHubConnection, isGitHubClientConfigured } from '../services/githubAuth';
import { syncLinkedAccountsFromIdp, unlinkGitHubAtIdp } from '../services/idpAccountService';
import { accountUrl } from '../services/oidcService';
import { isTauri } from '../utils/tauri';
import { useAuthStore } from '../store/authStore';
import { openExternal } from '../utils/openExternal';
import { useNotificationStore } from '../store/notificationStore';

const POLL_MS = 3000;
const POLL_GIVE_UP_S = 180;
const RETRY_MS = 2000;
const SIGN_IN_RETRIES = 3;

/**
 * The last GitHub connection this session saw. Opening the Git page used to
 * start from "Checking…" and a placeholder icon every time, then swap in the
 * avatar once the IdP answered — the picture "reloaded" on every visit (owner,
 * 04.10.2026). The page now starts from what it last knew and refreshes
 * quietly behind it, the way a mail client shows the inbox before syncing.
 * Memory only: it holds a login and a picture URL, never a token.
 */
let lastConn = null; // { owner: Apiboo user id or '', conn }
export function __resetGitHubLinkCache() {
  lastConn = null;
}
const ownerKey = () => String(useAuthStore.getState().user?.id ?? '');
// Only ever shown back to the same Apiboo user (a shared computer, a switched account).
const cachedConn = () => (lastConn && lastConn.owner === ownerKey() ? lastConn.conn : null);

/**
 * The GitHub link (git sync card 8 moved it from Settings to the Git page).
 * The IdP owns it (identity + repo token); we mirror it locally for git sync.
 * Mounting re-syncs from the IdP (picks up a link just made in the browser).
 * Linking opens the hosted account page and polls the IdP until the link
 * appears or the user cancels; unlinking goes through the IdP too.
 */
export default function useGitHubLink() {
  const [conn, setConnState] = useState(cachedConn); // null = still checking (first visit only)
  const setConn = useCallback((next) => {
    lastConn = { owner: ownerKey(), conn: next };
    setConnState(next);
  }, []);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [waiting, setWaiting] = useState(false);
  const pollRef = useRef(null);

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  // Returns false when the IdP could not be reached (the sync answers
  // `undefined` then), so a caller can retry.
  const refresh = useCallback(async () => {
    setLoading(true);
    let reached = false;
    try {
      reached = (await syncLinkedAccountsFromIdp()) !== undefined;
    } catch {
      /* offline or signed out — the local mirror still answers */
    }
    try {
      setConn(await getGitHubConnection());
    } catch {
      setConn({ connected: false, login: '', avatarUrl: '' });
    } finally {
      setLoading(false);
    }
    return reached;
  }, [setConn]);

  // Re-read on mount AND whenever the Apiboo sign-in changes. A GitHub link
  // that already exists on the account must show as "Signed in as …" right
  // after signing in on a new device — the Git page is often already open (or
  // kept mounted) at that moment, and a mount-only read left it on "Not signed
  // in" with a pointless "Sign in" button (owner, 04.10.2026).
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  useEffect(() => {
    let alive = true;
    let timer = null;
    // Right after a sign-in the session may still be settling and the first
    // IdP read can fail: retry a few times (2 s apart) before giving up.
    const attempt = async (left) => {
      const reached = await refresh();
      if (alive && !reached && signedIn && left > 0) timer = setTimeout(() => attempt(left - 1), RETRY_MS);
    };
    attempt(signedIn ? SIGN_IN_RETRIES : 0);
    return () => {
      alive = false;
      if (timer) clearTimeout(timer);
      stopPolling();
    };
  }, [refresh, signedIn]);

  const connect = async () => {
    setError('');
    setWaiting(true);
    try {
      await openExternal(`${accountUrl()}?tab=profile`);
    } catch (err) {
      setError(err?.message || String(err));
    }
    let elapsed = 0;
    stopPolling();
    pollRef.current = setInterval(async () => {
      elapsed += POLL_MS / 1000;
      const github = await syncLinkedAccountsFromIdp().catch(() => null);
      if (github && github.token) {
        stopPolling();
        setWaiting(false);
        setConn(await getGitHubConnection());
      } else if (elapsed >= POLL_GIVE_UP_S) {
        // The browser was closed or the sign-in abandoned: say so, rather than
        // the wait just ending with nothing (owner, 06.10.2026).
        stopPolling();
        setWaiting(false);
        useNotificationStore.getState().error('The sign-in in the browser was not finished. Try again.', 'GitHub not linked');
      }
    }, POLL_MS);
  };

  const cancel = () => {
    stopPolling();
    setWaiting(false);
  };

  const disconnect = async () => {
    setError('');
    setBusy(true);
    try {
      await unlinkGitHubAtIdp();
      await refresh();
    } catch (err) {
      setError(err?.message || String(err));
    } finally {
      setBusy(false);
    }
  };

  return {
    conn,
    loading,
    busy,
    error,
    waiting,
    desktop: isTauri(),
    configured: isGitHubClientConfigured(),
    connect,
    cancel,
    disconnect,
    refresh,
  };
}
